import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PlayCircleIcon } from '@heroicons/react/24/outline';
import { useTrack } from '../../contexts/TrackContext';
import LandingSectionDivider from './LandingSectionDivider';
import WalkthroughCarousel from '../video/WalkthroughCarousel';
import YouTubeWalkthroughEmbed from '../video/YouTubeWalkthroughEmbed';
import {
  COGNIDOOR_YOUTUBE_CHANNEL,
  getLandingVideosForTrack,
  type LandingTrack,
} from '../../config/walkthroughVideos';

const TRACK_SUBCOPY: Record<LandingTrack, string> = {
  all: 'Full walkthroughs for McKinsey Solve assessments, BCG Casey, and AI case coaching — pick a video and see exactly how Cognidoor helps you prep.',
  mck: 'Step-by-step McKinsey walkthroughs for Sea Wolf, Red Rock, and the Lilli AI case coach.',
  bcg: 'Watch the full BCG Casey walkthrough before you run the simulator.',
};

function trackKey(track: string): LandingTrack {
  if (track === 'mck' || track === 'bcg') return track;
  return 'all';
}

export default function LandingWalkthroughsSection() {
  const { track } = useTrack();
  const landingTrack = trackKey(track);
  const videos = getLandingVideosForTrack(landingTrack);
  const singleVideo = videos.length === 1 ? videos[0] : null;

  return (
    <section
      id="walkthroughs"
      className="relative py-6 sm:py-10 bg-gradient-to-b from-white via-blue-50/30 to-white text-center px-4 sm:px-6 md:px-10 xl:px-20"
    >
      <LandingSectionDivider />
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.12),transparent_50%)]" />
      </div>

      <div className="relative mx-auto max-w-6xl">
        <div className="mb-3 flex justify-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-sky-200/80 bg-sky-50/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-sky-800">
            <PlayCircleIcon className="h-3.5 w-3.5" aria-hidden />
            Video walkthroughs
          </span>
        </div>

        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 section-title tracking-tight">
          See how it works on video
        </h2>
        <p className="max-w-3xl mx-auto text-base sm:text-lg text-slate-600 leading-relaxed mb-6 sm:mb-8">
          {TRACK_SUBCOPY[landingTrack]}
        </p>

        <AnimatePresence mode="wait">
          <motion.div
            key={landingTrack}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ type: 'spring', stiffness: 400, damping: 35 }}
          >
            {singleVideo ? (
              <div className="mx-auto max-w-4xl rounded-3xl border border-slate-200/80 bg-white/95 p-4 shadow-lg shadow-slate-200/40 sm:p-6">
                <YouTubeWalkthroughEmbed video={singleVideo} />
                <div className="mt-5 text-left border-t border-slate-100 pt-5">
                  <h3 className="text-base font-semibold text-slate-900 sm:text-lg">{singleVideo.title}</h3>
                  <p className="mt-1 text-sm text-slate-600">{singleVideo.description}</p>
                </div>
                <p className="mt-6 text-center text-sm text-slate-600">
                  <a
                    href={COGNIDOOR_YOUTUBE_CHANNEL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-sky-700 underline-offset-2 hover:text-sky-800 hover:underline"
                  >
                    More on YouTube
                  </a>
                </p>
              </div>
            ) : (
              <WalkthroughCarousel videos={videos} />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
